import React, { useMemo, useEffect, useState } from "react";
import { useAuth } from "@via/hooks/useAuth";
import AccountInfoForm from "@via/pages/authentication/self-service-signup/progress/account-info-form.tsx";
import UserAgreementForm from "@via/pages/authentication/self-service-signup/progress/user-agreement-form.tsx";
import PaymentMethodForm from "@via/pages/authentication/self-service-signup/progress/payment-method-form.tsx";
import ConfirmationScreen from "@via/pages/authentication/self-service-signup/progress/confirmation-screen.tsx";
import { StepProgress } from "@via/components/step-progress.tsx";
import FoundryConnectLogo from "@via/components/foundry-connect-logo.tsx";
import { OnboardingStep, RegistrationType } from "common";
import { useSearchParams } from "react-router-dom";
import { useGetInvitationCheck } from "@via/pages/organization/organization.api.tsx";
import { useConfigurations } from "@via/api/configuration";
import { OverlayLoading } from "@via/components/loading/loading.tsx";
import {
  OnboardingStepKey,
  resolveStepIndex,
  useAgreementStep,
} from "@via/pages/authentication/self-service-signup/progress/onboarding-steps.ts";

const STEPS: Record<
  OnboardingStepKey,
  { name: string; component: React.ElementType }
> = {
  account: { name: "Account Info", component: AccountInfoForm },
  agreement: { name: "User Agreement", component: UserAgreementForm },
  payment: { name: "Payment Method", component: PaymentMethodForm },
  confirmation: { name: "Confirmation", component: ConfirmationScreen },
};

const invitedStepKeys: OnboardingStepKey[] = ["account", "confirmation"];

interface SelfSignupProgressProps {
  email: string;
}

const SelfSignupProgress: React.FC<SelfSignupProgressProps> = ({ email }) => {
  const { user } = useAuth();
  const { data: configData, isLoading: isConfigLoading } = useConfigurations();
  const [currentStepKey, setCurrentStepKey] =
    useState<OnboardingStepKey>("account");
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const enablePayment = configData?.ENABLE_STRIPE ?? false;

  const { data: invitationDetails } = useGetInvitationCheck(token);

  // Determine if the user is invited
  const isInvited = useMemo(() => {
    return (
      invitationDetails ||
      (user?.registrationType === RegistrationType.Invitation &&
        user?.userOrganizations?.[0]?.role?.name !== "org_admin")
    );
  }, [invitationDetails, user]);

  // The agreements endpoint needs a session, which exists once the account is created.
  const { skipAgreement, isAgreementLoading } = useAgreementStep(
    Boolean(user?.id) && !isInvited
  );

  const stepKeys = useMemo<OnboardingStepKey[]>(() => {
    if (isInvited) {
      return invitedStepKeys;
    }

    return [
      "account",
      ...(skipAgreement ? [] : (["agreement"] as const)),
      ...(enablePayment ? (["payment"] as const) : []),
      "confirmation",
    ];
  }, [isInvited, enablePayment, skipAgreement]);

  const currentStepIndex = resolveStepIndex(stepKeys, currentStepKey);

  const steps = useMemo(() => {
    return stepKeys.map((key, index) => ({
      name: STEPS[key].name,
      isCompleted: index < currentStepIndex,
    }));
  }, [currentStepIndex, stepKeys]);

  const handleNextStep = () => {
    if (currentStepIndex < stepKeys.length - 1) {
      setCurrentStepKey(stepKeys[currentStepIndex + 1]);
    } else {
      console.log("Final step completed! Submitting form...");
    }
  };

  const handlePreviousStep = () => {
    if (currentStepIndex - 1 > -1) {
      setCurrentStepKey(stepKeys[currentStepIndex - 1]);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepKeys[stepIndex] !== "confirmation") return;
    if (steps[stepIndex].isCompleted) {
      setCurrentStepKey(stepKeys[stepIndex]);
    }
  };

  useEffect(() => {
    if (!user?.onboardingStep || isConfigLoading) {
      return;
    }

    if (
      user?.userOrganizations?.[0]?.status === "inactive" &&
      user?.userOrganizations?.[0]?.role?.name === "org_admin"
    ) {
      setCurrentStepKey("payment");
      return;
    }

    // A step that is not listed resolves to the one after it, see resolveStepIndex.
    if (user.onboardingStep === OnboardingStep.AccountCreated) {
      setCurrentStepKey("agreement");
    } else if (user.onboardingStep === OnboardingStep.AgreementsAccepted) {
      setCurrentStepKey("payment");
    } else if (user.onboardingStep === OnboardingStep.PaymentCompleted) {
      setCurrentStepKey("confirmation");
    }
  }, [
    user?.onboardingStep,
    user?.userOrganizations?.[0]?.status,
    enablePayment,
    isConfigLoading,
  ]);

  const CurrentStepComponent = STEPS[stepKeys[currentStepIndex]]?.component;

  return (
    <div className="min-h-screen flex flex-col items-center justify-start px-4 sm:px-6 lg:px-8">
      {isConfigLoading || isAgreementLoading ? <OverlayLoading /> : null}
      <FoundryConnectLogo />
      <div className="flex flex-col items-center justify-center w-full max-w-3xl">
        {user?.userOrganizations?.[0]?.status !== "inactive" &&
          !isAgreementLoading && (
            <StepProgress
              steps={steps}
              currentStepIndex={currentStepIndex}
              onStepClick={handleStepClick}
            />
          )}
        <div className="mt-10">
          {CurrentStepComponent && !isAgreementLoading && (
            <CurrentStepComponent
              onNextStep={handleNextStep}
              onPreviousStep={handlePreviousStep}
              email={email}
              mode="card"
              enablePayment={enablePayment}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SelfSignupProgress;
